import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Hero } from "../components/site/Hero";
import { Marquee, CTABand, SectionHead } from "../components/site/Blocks";
import { Reveal } from "../components/site/Motion";
import { OUTCOMES, SERVICES, PROCESS, INDUSTRIES, IMAGES } from "../data/content";

const Outcomes = () => (
  <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32" data-testid="home-outcomes">
    <SectionHead label="What we help you do" title={["Evidence for every", <span className="font-serif-i">decision that matters.</span>]} aside="We help clients cultivate customer relationships, improve differentiation and competitive advantage, evaluate offerings and refine marketing strategy." />
    <div className="mt-16 grid border-t border-black/15 sm:grid-cols-2 lg:grid-cols-4">
      {OUTCOMES.map((o, i) => (
        <Reveal key={o.k} delay={i * 0.08} className="group border-b border-black/15 py-8 sm:px-6 lg:border-b-0 lg:border-r last:border-r-0 first:pl-0">
          <p className="font-mono text-xs text-[var(--muted)]">{o.k}</p>
          <h3 className="mt-10 text-2xl font-semibold tracking-tight transition-colors group-hover:text-[var(--cobalt)]">{o.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{o.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const BentoCard = ({ s, i, className = "", children }) => (
  <Reveal delay={(i % 3) * 0.08} className={className}>
    <Link to={`/services#${s.slug}`} data-testid={`bento-service-${s.slug}`} className="group relative flex h-full flex-col justify-between overflow-hidden rounded-[4px] border border-black/10 bg-white p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(13,15,20,0.35)] sm:p-8">
      {children}
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-2xl font-semibold tracking-tight">{s.title}</h3>
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-black/15 transition-[background-color,transform,color] duration-300 group-hover:rotate-45 group-hover:bg-[var(--cobalt)] group-hover:text-white">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-[var(--muted)]">{s.short}</p>
      </div>
    </Link>
  </Reveal>
);

const Bento = () => (
  <section className="bg-[var(--paper-2)] py-24 sm:py-32" data-testid="home-services-bento">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <SectionHead label="Services" title={["Full-stack research,", "from field to strategy."]} />
      <div className="mt-16 grid gap-4 lg:grid-cols-12 lg:grid-rows-[minmax(260px,auto)_minmax(260px,auto)]">
        <BentoCard s={SERVICES[0]} i={0} className="lg:col-span-6 lg:row-span-2">
          <div className="relative mb-8 h-64 overflow-hidden rounded-[2px] lg:h-[360px]">
            <img src={IMAGES.tablet} alt="Data analysis on tablet" className="h-full w-full object-cover grayscale-[40%] transition-transform duration-[1.2s] group-hover:scale-105" />
            <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em]">Flagship</span>
          </div>
        </BentoCard>
        <BentoCard s={SERVICES[1]} i={1} className="lg:col-span-3" />
        <BentoCard s={SERVICES[2]} i={2} className="lg:col-span-3" />
        <BentoCard s={SERVICES[3]} i={3} className="lg:col-span-6">
          <p className="mb-8 font-serif-i text-5xl leading-none text-[var(--cobalt)] sm:text-6xl">Operations engine.</p>
        </BentoCard>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <BentoCard s={SERVICES[4]} i={4} />
        <BentoCard s={SERVICES[5]} i={5} />
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32" data-testid="home-process">
    <SectionHead label="How we work" title={["Four moves.", <span className="font-serif-i">Zero guesswork.</span>]} aside="A disciplined, transparent workflow with quality checks at every step, so you get answers you can defend in the boardroom." />
    <div className="relative mt-16 grid gap-10 md:grid-cols-4 md:gap-6">
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} className="absolute left-0 right-0 top-[22px] hidden h-px origin-left bg-[var(--ink)] md:block" />
      {PROCESS.map((p, i) => (
        <Reveal key={p.n} delay={0.2 + i * 0.15} className="relative">
          <span className="relative z-10 grid h-11 w-11 place-items-center rounded-full border border-[var(--ink)] bg-[var(--paper)] font-mono text-xs">{p.n}</span>
          <h3 className="mt-6 text-3xl font-semibold tracking-tight">{p.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{p.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-[var(--ink)] py-24 text-[var(--paper)] sm:py-32" data-testid="home-industries">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-400">Industries</p>
      <div className="mt-10 border-t border-white/15">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.key} delay={i * 0.05}>
            <Link to="/industries" data-testid={`home-industry-${ind.key}`} className="group flex items-center justify-between gap-6 border-b border-white/15 py-6 sm:py-8">
              <span className="flex items-baseline gap-5">
                <span className="font-mono text-xs text-zinc-400">0{i + 1}</span>
                <span className="text-3xl font-semibold tracking-tight transition-[transform,color] duration-500 group-hover:translate-x-3 group-hover:text-white sm:text-5xl lg:text-6xl">{ind.name}</span>
              </span>
              <span className="hidden max-w-xs text-sm text-zinc-400 lg:block">{ind.cases.join(" · ")}</span>
              <ArrowUpRight className="h-6 w-6 shrink-0 text-zinc-400 transition-[transform,color] group-hover:rotate-45 group-hover:text-[var(--cobalt)]" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TeamPromise = () => (
  <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32" data-testid="home-promise">
    <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
      <Reveal className="lg:col-span-5">
        <div className="relative aspect-[4/5] overflow-hidden rounded-[4px]">
          <img src={IMAGES.whiteboard} alt="Team presenting research findings" className="h-full w-full object-cover grayscale-[30%]" />
        </div>
      </Reveal>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal>
          <p className="text-3xl font-medium leading-[1.15] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Young, dynamic and <span className="font-serif-i text-[var(--cobalt)]">enthusiastic</span> — backed by experts across domains who have done this for years.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {[["Quality", "Best-in-class checks"], ["Speed", "Agile, responsive teams"], ["Value", "Competitive pricing"]].map(([a, b], i) => (
            <Reveal key={a} delay={i * 0.1} className="border-t border-black/20 pt-4">
              <p className="text-xl font-semibold">{a}</p>
              <p className="mt-1 text-sm text-[var(--muted)]">{b}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Outcomes />
      <Bento />
      <Process />
      <Industries />
      <TeamPromise />
      <CTABand />
    </main>
  );
}
